import { defineStore } from 'pinia';
import { useUser } from './user';
import { useApi } from '@/lib/api';
import { tagOrderIndex, sortByTagOrder } from '@lib/tag-order';
import { useGlobalStore } from '@/store/global-store-base';
import { computed, type ComputedRef } from 'vue';
import type { TagCategoryDto, TagDto } from '@/ApiClient';
import { TagCategoryUniqueness } from '@/ApiClient';
import { useHierarchy } from '@/store/hierarchy.ts';

export const useTags = defineStore('tags', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.tag_GetTags,
    dto: 'TagCategoryDto',
    create: api.tag_AddTagCategory,
    signalR: 'TagUpdate',
  });
  const { loaded } = globalStoreBase;

  const baseCategory: ComputedRef<TagCategoryDto> = computed(() => {
    // Not loaded[0]: the list follows the brand's manual category order, so the first entry is
    // whatever a user dragged (or the migration backfilled) to the top — not the base category.
    return loaded.value.find((category) => category.baseCategory) ?? loaded.value[0];
  });

  const any: ComputedRef<boolean> = computed(() => {
    return Boolean(loaded.value.length && loaded.value.find((tagGroup) => tagGroup.tags && tagGroup.tags.length > 0));
  });

  const sorted: ComputedRef<TagCategoryDto[]> = computed(() => {
    // Categories and tags arrive from the backend in their explicit (manual) order. We only keep
    // product-level categories grouped before article-level ones (tag pickers rely on this); the
    // stable sort preserves the backend order within each group. Copies avoid mutating `loaded`.
    return [...loaded.value]
      .sort((a, b) => Number(b.productLevel) - Number(a.productLevel))
      .map((tagGroup) => ({ ...tagGroup, tags: tagGroup.tags ? [...tagGroup.tags] : tagGroup.tags }));
  });

  const flattened: ComputedRef<TagDto[]> = computed(() => {
    return loaded.value.flatMap((tagGroup) => tagGroup.tags || []);
  });

  const byId: ComputedRef<Record<number, TagDto>> = computed(() => {
    return Object.fromEntries(flattened.value.map((tag) => [tag.id, tag]));
  });

  const categoryById: ComputedRef<Record<number, TagCategoryDto>> = computed(() => {
    return Object.fromEntries(loaded.value.map((category) => [category.id, category]));
  });

  // Global display rank of every tag: product-level categories first, then article-level, each in the
  // brand's manual category order, tags within a category in their manual order. Used to order the pills
  // a product/article *has* so assigned tags follow the same order as the palette.
  const orderIndexById: ComputedRef<Map<number, number>> = computed(() => tagOrderIndex(sorted.value));

  function sortByOrder<T extends { id: number }>(tags: T[]): T[] {
    return sortByTagOrder(tags, orderIndexById.value);
  }

  async function addNewTag(name: string, color: string, tagCategoryId: number) {
    const brandId = useUser().brandId;

    const tagWithTagCategoryId = {
      name,
      color,
      tagCategoryId,
    };

    const tag = await api.tag_AddTag(brandId, tagWithTagCategoryId);

    const categoryIndex = loaded.value.findIndex((category) => category.id === tagCategoryId);

    if (categoryIndex > -1) {
      if (!loaded.value[categoryIndex].tags) loaded.value[categoryIndex].tags = [];

      loaded.value[categoryIndex].tags?.push(tag);
    }

    return tag;
  }

  async function updateTag(tagId: number, name: string, color: string, hierarchyNodeIds: number[]) {
    const brandId = useUser().brandId;

    const tag = await api.tag_UpdateTag(brandId, tagId, { color, name, hierarchyNodeIds });

    const categoryIndex = loaded.value.findIndex((category) => category.id === tag.tagCategoryId);
    if (categoryIndex > -1) {
      const tagIndexInCategory = loaded.value[categoryIndex].tags?.findIndex((tag) => tag.id === tagId);
      if (tagIndexInCategory !== undefined && tagIndexInCategory > -1) {
        loaded.value[categoryIndex].tags?.splice(tagIndexInCategory, 1, tag);
      }
    }

    return tag;
  }

  async function deleteTag(tagId: number) {
    const brandId = useUser().brandId;

    await api.tag_DeleteTag(brandId, tagId);

    const category = loaded.value.find((c) => c.tags?.some((t) => t.id == tagId));
    if (category) {
      const index = category.tags!.findIndex((t) => t.id == tagId);
      category.tags!.splice(index, 1);
    }
  }

  async function updateCategory(
    tagCategoryId: number,
    name: string,
    hierarchyNodeIds: number[],
    uniqueness: TagCategoryUniqueness,
    includeInArticleNumberGeneration: boolean
  ) {
    const brandId = useUser().brandId;

    const category = await api.tag_UpdateTagCategory(brandId, tagCategoryId, {
      name,
      hierarchyNodeIds,
      uniqueness,
      includeInArticleNumberGeneration,
    });

    const index = loaded.value.findIndex((c) => c.id === category.id);
    if (index > -1) {
      loaded.value.splice(index, 1, category);
    }

    return category;
  }

  async function reorderCategories(orderedCategoryIds: number[]) {
    const brandId = useUser().brandId;
    await api.tag_ReorderTagCategories(brandId, { tagCategoryIds: orderedCategoryIds });
    loaded.value.sort((a, b) => orderedCategoryIds.indexOf(a.id) - orderedCategoryIds.indexOf(b.id));
  }

  async function reorderTags(tagCategoryId: number, orderedTagIds: number[]) {
    const brandId = useUser().brandId;
    await api.tag_ReorderTags(brandId, tagCategoryId, { tagIds: orderedTagIds });
    const category = loaded.value.find((c) => c.id === tagCategoryId);
    category?.tags?.sort((a, b) => orderedTagIds.indexOf(a.id) - orderedTagIds.indexOf(b.id));
  }

  function groupByTagId(id: number) {
    for (const category of loaded.value) {
      if (category.tags?.map((t) => t.id).includes(id)) {
        return category;
      }
    }
  }

  function idsToTags(ids: number[]) {
    const tags = [];

    for (const id of ids) {
      const tag = byId.value[id];

      if (tag) tags.push(tag);
    }

    return tags;
  }

  const categoryAvailableOnAllNodes = computed(() => {
    const allRestrictedCategories = useHierarchy().fullNodes.flatMap((node) => node.tagCategoryIds);
    return loaded.value.filter((tagCategory) => !allRestrictedCategories.includes(tagCategory.id));
  });

  // Categories the node can see, each carrying only the tags it can see — a category whose every tag
  // is restricted elsewhere still shows, empty, the same way an unused category does.
  function visibleForProduct(hierarchyNodeId?: number) {
    return sorted.value
      .filter((tagGroup) => isVisible(tagGroup, hierarchyNodeId))
      .map((tagGroup) => ({
        ...tagGroup,
        tags: (tagGroup.tags ?? []).filter((tag) => isTagVisible(tag, hierarchyNodeId)),
      }));
  }

  function isVisible(tagGroup: TagCategoryDto, hierarchyNodeId?: number) {
    if (tagGroup.hierarchyNodeIds?.length === 0) return true;
    if (!hierarchyNodeId) return false;

    const visibleTagCategories = useHierarchy()
      .getNodeAndAncestors(hierarchyNodeId)
      .flatMap((node) => node.tagCategoryIds);

    return visibleTagCategories.includes(tagGroup.id);
  }

  // A tag narrows its category's reach, never escapes it: with no nodes of its own it simply follows
  // the category. Mirrors useCustomFields().isVisible.
  function isTagVisible(tag: TagDto, hierarchyNodeId?: number) {
    // Keyed lookup rather than groupByTagId: the mass-edit panel asks this once per tag per selected
    // article, and a scan of every category's tags there is thousands of throwaway arrays.
    const tagGroup = categoryById.value[tag.tagCategoryId];
    if (!tagGroup || !isVisible(tagGroup, hierarchyNodeId)) return false;

    if (tag.hierarchyNodeIds?.length === 0) return true;
    if (!hierarchyNodeId) return false;

    return useHierarchy()
      .getNodeAndAncestors(hierarchyNodeId)
      .some((node) => tag.hierarchyNodeIds.includes(node.id));
  }

  return {
    ...globalStoreBase,
    loaded,
    baseCategory,
    any,
    sorted,
    flattened,
    byId,
    categoryById,
    orderIndexById,
    sortByOrder,
    categoryAvailableOnAllNodes,
    visibleForProduct,
    isVisible,
    isTagVisible,
    addNewCategory: globalStoreBase.create,
    addNewTag,
    updateTag,
    deleteTag,
    updateCategory,
    reorderCategories,
    reorderTags,
    groupByTagId,
    idsToTags,
  };
});
